import React, { useRef, useEffect, useState } from 'react';
import { VideoQuality } from 'livekit-client';

export function VideoTile({
  track,
  publication,
  participant,
  id,
  isDragging,
  onTileDragStart,
  onTileDragEnd,
  onTileDrop,
}) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (!track || !videoRef.current) return;

    // Attach track to video element
    track.attach(videoRef.current);

    // Track dimension changes
    const updateDimensions = () => {
      if (videoRef.current) {
        setDimensions({
          width: videoRef.current.videoWidth,
          height: videoRef.current.videoHeight,
        });
      }
    };

    videoRef.current.addEventListener('loadedmetadata', updateDimensions);
    videoRef.current.addEventListener('resize', updateDimensions);

    return () => {
      track.detach(videoRef.current);
      if (videoRef.current) {
        videoRef.current.removeEventListener('loadedmetadata', updateDimensions);
        videoRef.current.removeEventListener('resize', updateDimensions);
      }
    };
  }, [track]);

  // Set video quality preference based on container size for adaptive streaming
  useEffect(() => {
    if (!publication || !containerRef.current) return;

    // Request highest quality for the observer
    // This tells LiveKit we want the best available quality
    if (publication.setVideoQuality) {
      publication.setVideoQuality(VideoQuality.HIGH);
    }

    // Also set video dimensions hint for adaptive streaming
    const updateQualityHint = () => {
      if (containerRef.current && publication.setVideoDimensions) {
        const rect = containerRef.current.getBoundingClientRect();
        // Request dimensions based on container size, but request at least 720p
        const width = Math.max(rect.width * window.devicePixelRatio, 1280);
        const height = Math.max(rect.height * window.devicePixelRatio, 720);
        publication.setVideoDimensions({ width, height });
      }
    };

    updateQualityHint();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateQualityHint);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [publication]);

  const participantName = participant?.identity || 'Unknown';
  const trackSource = publication?.source || 'unknown';
  const isScreenShare = trackSource === 'screen_share' || trackSource === 'screen_share_audio';

  const draggable = typeof onTileDragStart === 'function';

  return (
    <div
      ref={containerRef}
      className={`video-tile ${isScreenShare ? 'screen-share' : ''} ${isDragging ? 'dragging' : ''} ${draggable ? 'draggable' : ''} ${dragOver ? 'drag-over' : ''}`}
      draggable={draggable}
      onDragStart={draggable ? () => onTileDragStart(id) : undefined}
      onDragEnd={draggable ? () => { setDragOver(false); onTileDragEnd?.(); } : undefined}
      onDragOver={draggable ? (e) => e.preventDefault() : undefined}
      onDragEnter={draggable ? () => setDragOver(true) : undefined}
      onDragLeave={draggable ? () => setDragOver(false) : undefined}
      onDrop={draggable ? (e) => { e.preventDefault(); setDragOver(false); onTileDrop?.(id); } : undefined}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        draggable={false}
      />
      <div className="video-tile-label">
        <span className="participant-name">{participantName}</span>
        {isScreenShare && <span className="track-type">Screen Share</span>}
        {dimensions.width > 0 && (
          <span className="track-resolution">
            {dimensions.width}x{dimensions.height}
          </span>
        )}
      </div>
    </div>
  );
}
